"use client";

import { createContext, useContext, useState, useEffect } from "react";
import { useRouter } from "next/navigation";
import { Dictionary, getDictionary, Language } from "./dictionaries";

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: Dictionary;
  dictionary: Dictionary;
  isChanging: boolean;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export function LanguageProvider({
  children,
  defaultLanguage,
}: {
  children: React.ReactNode;
  defaultLanguage: Language;
}) {
  const [language, setLanguageState] = useState<Language>(defaultLanguage);
  const [isChanging, setIsChanging] = useState(false);

  useEffect(() => {
    // Initial hydration check — sync with localStorage if it differs from cookie default.
    const savedLang = localStorage.getItem("app-lang") as Language;
    if (savedLang && (savedLang === "pl" || savedLang === "en") && savedLang !== language) {
      setLanguageState(savedLang);
      document.cookie = `lang=${savedLang}; path=/; max-age=31536000; SameSite=Lax`;
    } else if (!savedLang && defaultLanguage) {
      document.cookie = `lang=${defaultLanguage}; path=/; max-age=31536000; SameSite=Lax`;
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Sync state if defaultLanguage changes from server
  useEffect(() => {
    const savedLang = localStorage.getItem("app-lang") as Language;
    if (!savedLang && language !== defaultLanguage) {
      setLanguageState(defaultLanguage);
    }
  }, [defaultLanguage, language]);

  const router = useRouter();

  const setLanguage = (lang: Language) => {
    setIsChanging(true);
    setLanguageState(lang);
    localStorage.setItem("app-lang", lang);
    document.cookie = `lang=${lang}; path=/; max-age=31536000; SameSite=Lax`;
    router.refresh();

    setTimeout(() => {
      setIsChanging(false);
    }, 1000);
  };

  const dictionary = getDictionary(language);
  const t = dictionary;

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t, dictionary, isChanging }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error("useLanguage must be used within a LanguageProvider");
  }
  return context;
}
